import { useState, useEffect } from "react";
import "../../assets/scss/feature-types/add-feature-type.scss";
import axiosConfig from "../../services/axiosConfig";
import setHeader from "../../components/Functions/setHeader";
import SweetAlert from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
// Import reusable components
import NormalDropDown from "../../components/theme/NormalDropDown";
import SearchableDropdownInput from "../../components/theme/SearchableDropdownInput";
import TextInput from "../../components/theme/TextInput";

const AddFeatureTypes = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [typeData, setTypeData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [productData, setProductData] = useState([]);
  const user_id = localStorage.getItem("userId");
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    type: "",
    category: "",
    product_id: "",
    userId: "",
    from_date: "",
    to_date: "",
    IsDatecheck: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchFeatureTypeData();
    }
  }, [id, isEditMode]);

  const fetchFeatureTypeData = async () => {
    setIsDataLoading(true);
    try {
      const response = await axiosConfig.get(`get-feature-type/${id}`, setHeader());
      if (!response.data.error) {
        const featureTypeData = response.data.data || response.data;
        
        // Handle both array and single object responses
        const data = Array.isArray(featureTypeData) ? featureTypeData[0] : featureTypeData;
        
        const newFormData = {
          type: data.type || data.f_type || "",
          category: data.category || "",
          product_id: data.product_id || "",
          userId: data.userId || user_id,
          from_date: data.from_date || "",
          to_date: data.to_date || "",
          IsDatecheck: Boolean(data.IsDatecheck),
        };
        
        setFormData(newFormData);
        
        // If category exists, fetch products for that category
        if (newFormData.category) {
          getCategoryWiseProducts(newFormData.category);
        }
      } else {
        SweetAlert.fire({
          title: "Error",
          text: "Failed to fetch feature type data",
          icon: "error",
        });
      }
    } catch (error) {
      console.error('Error fetching feature type data:', error);
      SweetAlert.fire({
        title: "Error",
        text: "An error occurred while fetching feature type data",
        icon: "error",
      });
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "IsDatecheck") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
        // Clear dates if unchecked
        from_date: checked ? prev.from_date : "",
        to_date: checked ? prev.to_date : "",
      }));
    } else if (name === "category") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        // Reset product selection when category changes
        product_id: "",
      }));
      
      // Fetch products for selected category
      if (value) {
        getCategoryWiseProducts(value);
      } else {
        setProductData([]);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.type) {
      SweetAlert.fire({
        title: "Validation Error",
        text: "Feature type is required",
        icon: "warning",
      });
      return;
    }

    if (!formData.category) {
      SweetAlert.fire({
        title: "Validation Error",
        text: "Category selection is required",
        icon: "warning",
      });
      return;
    }

    if (!formData.product_id) {
      SweetAlert.fire({
        title: "Validation Error",
        text: "Product selection is required",
        icon: "warning",
      });
      return;
    }

    // Date validation when date check is enabled
    if (formData.IsDatecheck) {
      if (!formData.from_date) {
        SweetAlert.fire({
          title: "Validation Error",
          text: "From date is required when date check is enabled",
          icon: "warning",
        });
        return;
      }

      if (!formData.to_date) {
        SweetAlert.fire({
          title: "Validation Error",
          text: "To date is required when date check is enabled",
          icon: "warning",
        });
        return;
      }

      // Check if from_date is before to_date
      if (new Date(formData.from_date) >= new Date(formData.to_date)) {
        SweetAlert.fire({
          title: "Validation Error",
          text: "From date must be before to date",
          icon: "warning",
        });
        return;
      }
    }

    setIsLoading(true);

    const submitData = {
      type: parseInt(formData.type),
      category: parseInt(formData.category),
      product_id: parseInt(formData.product_id),
      userId: user_id,
      from_date: formData.from_date || null,
      to_date: formData.to_date || null,
      IsDatecheck: formData.IsDatecheck ? 1 : 0,
    };

    // For edit mode, add the ID
    if (isEditMode) {
      submitData.f_id = id;
    }

    const apiEndpoint = isEditMode ? "update-feature-type" : "save-feature-types";
    const successMessage = isEditMode
      ? "Feature type updated successfully"
      : "Feature type added successfully";

    axiosConfig
      .post(apiEndpoint, submitData, setHeader())
      .then((res) => {
        setIsLoading(false);

        if (!res.data.error) {
          SweetAlert.fire({
            title: "Success",
            text: res.data.messages || successMessage,
            icon: "success",
          });
          // Reset form after successful submission (only for add mode)
          if (!isEditMode) {
            handleReset();
            navigate('/pages/feature-type/feature-type-list')
          } else {
              navigate('/pages/feature-type/feature-type-list')
            fetchFeatureTypeData();
          }
        } else {
          SweetAlert.fire({
            title: "Oops!",
            text:
              res.data.messages ||
              `Failed to ${isEditMode ? "update" : "add"} feature type. Try again!`,
            icon: "error",
          });
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error('Backend error:', error);
        SweetAlert.fire({
          title: "Error",
          text: `An error occurred while ${isEditMode ? "updating" : "adding"} the feature type`,
          icon: "error",
        });
      });
  };

  const handleReset = () => {
    if (isEditMode) {
      // For edit mode, reset to original data
      fetchFeatureTypeData();
    } else {
      // For add mode, reset to empty form
      setFormData({
        type: "",
        category: "",
        product_id: "",
        userId: "",
        from_date: "",
        to_date: "",
        IsDatecheck: false,
      });
      // Clear products when resetting
      setProductData([]);
    }
  };

  useEffect(() => {
    getTypeData();
    getCategoryData();
  }, []);

  const getTypeData = () => {
    axiosConfig.get("all-sections", setHeader()).then((res) => {
      setTypeData(res.data.data);
    }).catch((error) => {
      console.error('Error fetching type data:', error);
    });
  };

  const getCategoryData = () => {
    axiosConfig.get("active-category-list", setHeader()).then((res) => {
      setCategoryData(res.data.data);
    }).catch((error) => {
      console.error('Error fetching category data:', error);
    });
  };

  const getCategoryWiseProducts = (categoryId) => {
    if (!categoryId) {
      setProductData([]);
      return;
    }
    
    axiosConfig.get(`category-wise-product/${categoryId}`, setHeader()).then((res) => {
      setProductData(res.data.data || []);
    }).catch((error) => {
      console.error('Error fetching category-wise products:', error);
      setProductData([]);
    });
  };

  if (isDataLoading) {
    return (
      <div className="AddFeatureTypeMainWrapper">
        <div className="feature-type-form-container">
          <div className="loading-state">Loading feature type data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="AddFeatureTypeMainWrapper">
      <div className="feature-type-form-container">
        <h2 className="form-title">
          {isEditMode ? "Edit Feature Type" : "Add Feature Type"}
        </h2>

        <form onSubmit={handleSubmit} className="feature-type-form">
          {/* Feature Type Dropdown */}
          <SearchableDropdownInput
            id="type"
            name="type"
            label="Feature Type"
            value={formData.type}
            onChange={handleInputChange}
            options={typeData.map(sectionType => ({
              value: sectionType.s_id,
              label: sectionType.section_name || `Section ${sectionType.s_id}`
            }))}
            placeholder="Select Feature Type"
            required={true}
            noOptionsMessage="No Feature Type found"
          />

          {/* Category Dropdown */}
          <SearchableDropdownInput
            id="category"
            name="category"
            label="Category"
            value={formData.category}
            onChange={handleInputChange}
            options={categoryData.map(category => ({
              value: category.value,
              label: category.label || `Category ${category.id}`
            }))}
            placeholder="Select Category"
            required={true}
            noOptionsMessage="No Categories found"
          />

          {/* Product Dropdown - Only show products when category is selected */}
          <SearchableDropdownInput
            id="product_id"
            name="product_id"
            label="Product"
            value={formData.product_id}
            onChange={handleInputChange}
            options={productData.map(product => ({
              value: product.id,
              label: product.productName || `Product ${product.id}`
            }))}
            placeholder={formData.category ? "Search and select product..." : "Please select a category first"}
            required={true}
            noOptionsMessage={formData.category ? "No products found for this category" : "Please select a category first"}
            disabled={!formData.category}
          />

          {/* Date Checkbox */}
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="IsDatecheck"
                checked={formData.IsDatecheck}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="checkbox-text">Add expiry dates</span>
            </label>
          </div>

          {/* Conditional Date Fields */}
          {formData.IsDatecheck && (
            <>
              <TextInput
                id="from_date"
                name="from_date"
                label="From Date"
                type="date"
                value={formData.from_date}
                onChange={handleInputChange}
                required={formData.IsDatecheck}
                disabled={!formData.IsDatecheck}
              />

              <TextInput
                id="to_date"
                name="to_date"
                label="To Date"
                type="date"
                value={formData.to_date}
                onChange={handleInputChange}
                required={formData.IsDatecheck}
                disabled={!formData.IsDatecheck}
              />
            </>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading
                ? `${isEditMode ? "Updating" : "Submiting..."}`
                : `${isEditMode ? "Update" : "Submit"}`}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="btn btn-secondary"
              disabled={isLoading}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFeatureTypes;